import { toRef, type Component } from "vue";
import type { RouteNamedMap } from "vue-router/auto-routes";

import ArrowRightStartOnRectangleIcon from "@/icons/heroicons/arrow-right-start-on-rectangle-micro.svg";
import BellIcon from "@/icons/heroicons/bell-micro.svg";
import BookOpenIcon from "@/icons/heroicons/book-open-micro.svg";
import CalendarIcon from "@/icons/heroicons/calendar-micro.svg";
import ChartBarIcon from "@/icons/heroicons/chart-bar-micro.svg";
import ChatBubbleLeftRightIcon from "@/icons/heroicons/chat-bubble-left-right-micro.svg";
import ClipboardIcon from "@/icons/heroicons/clipboard-micro.svg";
import CodeBracketIcon from "@/icons/heroicons/code-bracket-micro.svg";
import ComputerDesktopIcon from "@/icons/heroicons/computer-desktop-micro.svg";
import CogIcon from "@/icons/heroicons/cog-6-tooth-micro.svg";
import SunIcon from "@/icons/heroicons/sun-micro.svg";
import DocumentTextIcon from "@/icons/heroicons/document-text-micro.svg";
import ArchiveBoxIcon from "@/icons/heroicons/archive-box-micro.svg";
import CreditCardIcon from "@/icons/heroicons/credit-card-micro.svg";
import DocumentArrowUpIcon from "@/icons/heroicons/document-arrow-up-micro.svg";
import EnvelopeIcon from "@/icons/heroicons/envelope-micro.svg";
import ExclamationTriangleIcon from "@/icons/heroicons/exclamation-triangle-micro.svg";
import EyeIcon from "@/icons/heroicons/eye-micro.svg";
import GiftIcon from "@/icons/heroicons/gift-micro.svg";
import HouseIcon from "@/icons/heroicons/house-micro.svg";
import InboxIcon from "@/icons/heroicons/inbox-micro.svg";
import KeyIcon from "@/icons/heroicons/key-micro.svg";
import LockClosedIcon from "@/icons/heroicons/lock-closed-micro.svg";
import MoonIcon from "@/icons/heroicons/moon-micro.svg";
import PaintBrushIcon from "@/icons/heroicons/paint-brush-micro.svg";
import PlayIcon from "@/icons/heroicons/play-micro.svg";
import RSSIcon from "@/icons/heroicons/rss-micro.svg";
import ShoppingBagIcon from "@/icons/heroicons/shopping-bag-micro.svg";
import SparkleIcon from "@/icons/heroicons/sparkles-micro.svg";
import StackMicroIcon from "@/icons/heroicons/stack-micro.svg";
import TagIcon from "@/icons/heroicons/tag-micro.svg";
import UserGroupIcon from "@/icons/heroicons/user-group-micro.svg";
import UserIcon from "@/icons/heroicons/user-micro.svg";
import ViewfinderIcon from "@/icons/heroicons/viewfinder.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";
import StripeIcon from "@/icons/brands/stripe.svg";
import internalApi from "@/lib/api/internal-base";
import { IS_ADMIN } from "@/lib/environment";
import { useExport } from "@/lib/exportable/lib";
import type { RouteLocation } from "@/router/types";
import { useAccountStore, useNewsletter } from "@/store/stores/account";
import { useStore as useUiStatesStore } from "@/store/stores/ui-states";
import type { Permission } from "@/types/account";
import type { Newsletter } from "@/types/newsletter";

type BaseRouteConfiguration = {
  title: string;
  matches?: readonly string[];
  icon: Component;
  color?: string;
  feature?: Newsletter["enabled_features"][number];
  permission?: keyof Permission["permissions"];
  hide?: (
    permissions: Permission["permissions"],
    newsletter: Newsletter
  ) => boolean;
};

export type RouteConfiguration = BaseRouteConfiguration & {
  route: RouteLocation;
};

type SpecialActionConfiguration = BaseRouteConfiguration & {
  action: () => void;
};

export type AnyRouteConfiguration =
  | RouteConfiguration
  | SpecialActionConfiguration;

export type RouteToConfiguration = {
  [key in keyof Omit<RouteNamedMap, "/[...path]">]?: RouteConfiguration;
} & {
  [key in `/__special_actions/${string}`]: SpecialActionConfiguration;
};

const HIDE_IF_NOT_ADMIN = () => {
  return !IS_ADMIN;
};

export const SEARCH_CONTEXT = {
  "/__special_actions/permanently-enable": {
    title: "Permanently enable",
    icon: SunIcon,
    action: async () => {
      const newsletter = useNewsletter();
      const privateNote = window.prompt("Private note (optional):");
      // If user cancels the prompt, don't proceed
      if (privateNote === null) {
        return;
      }
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: {
          status: "permanently_active",
          private_notes: privateNote || undefined,
        },
      });
      window.location.href = "/home";
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/permanently-disable": {
    title: "Permanently disable",
    icon: MoonIcon,
    action: async () => {
      const newsletter = useNewsletter();
      const privateNote = window.prompt("Private note (optional):");
      // If user cancels the prompt, don't proceed
      if (privateNote === null) {
        return;
      }
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: {
          status: "permanently_disabled",
          private_notes: privateNote || undefined,
        },
      });
      window.location.href = "/home";
    },
    hide: (_p, n) => !IS_ADMIN || n.status === "permanently_disabled",
  },
  "/__special_actions/toggle-dark-mode": {
    title: "Toggle dark mode",
    icon: MoonIcon,
    matches: ["Dark mode", "Light mode"],
    action: () => {
      const uiStates = useUiStatesStore();
      const theme = toRef(uiStates, "appTheme");
      theme.value = theme.value === "dark" ? "light" : "dark";
    },
  },
  "/__special_actions/switch-to-postmark": {
    title: "Switch to Postmark",
    icon: HouseIcon,
    action: async () => {
      const newsletter = useNewsletter();
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: { delivery_provider: "postmark" },
      });
      window.location.href = "/home";
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/switch-to-mailgun": {
    title: "Switch to Mailgun",
    icon: HouseIcon,
    action: async () => {
      const newsletter = useNewsletter();
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: { delivery_provider: "mailgun" },
      });
      window.location.href = "/home";
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/switch-to-sendgrid": {
    title: "Switch to Sendgrid",
    icon: HouseIcon,
    action: async () => {
      const newsletter = useNewsletter();
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: { delivery_provider: "sendgrid" },
      });
      window.location.reload();
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/start-onboarding-flow": {
    title: "Restart onboarding flow",
    icon: HouseIcon,
    action: async () => {
      const newsletter = useNewsletter();
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: {
          onboarding_data: {
            version: "2025-09-16",
            status: "not_started",
          },
        },
      });
      window.location.href = "/home";
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/resume-onboarding-flow": {
    title: "Resume onboarding flow",
    icon: HouseIcon,
    action: async () => {
      const newsletter = useNewsletter();
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: {
          onboarding_data: {
            ...(newsletter.value.onboarding_data ?? {}),
            version: "2025-09-16",
            status: "answered_questions",
          },
        },
      });
      window.location.href = "/home";
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/set-private-note": {
    title: "Set private note",
    icon: DocumentTextIcon,
    action: async () => {
      const newsletter = useNewsletter();
      const privateNote = window.prompt("Private note:");
      if (privateNote === null) {
        return;
      }
      await internalApi.put("/newsletters/{id}", {
        params: { path: { id: newsletter.value.id } },
        body: {
          private_notes: privateNote || undefined,
        },
      });
      window.location.reload();
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/reset-local-storage": {
    title: "Reset local storage",
    icon: XMarkIcon,
    action: () => {
      localStorage.clear();
      window.location.reload();
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/__special_actions/logout": {
    title: "Log out",
    icon: ArrowRightStartOnRectangleIcon,
    matches: ["Logout", "Sign out"],
    action: () => {
      useAccountStore().logout();
    },
  },
  "/__special_actions/export": {
    title: "Export your data",
    icon: DocumentArrowUpIcon,
    action: () => {
      useExport([
        "subscribers",
        "emails",
        "events",
        "comments",
        "conversations",
        "mentions",
        "requests",
        "stripe_subscriptions",
        "stripe_products",
        "stripe_customers",
        "stripe_prices",
        "stripe_charges",
      ]).process({});
    },
  },
  "/__special_actions/open-customer-in-stripe": {
    title: "Open customer in Stripe",
    icon: StripeIcon,
    matches: ["Open customer in Stripe", "Stripe customer"],
    hide: HIDE_IF_NOT_ADMIN,
    action: () => {
      const accountStore = useAccountStore();
      const customerId = accountStore.account?.stripe_customer_id;
      if (customerId) {
        window.open(
          `https://dashboard.stripe.com/customers/${customerId}`,
          "_blank"
        );
      }
    },
  },
  "/__special_actions/open-account-in-stripe": {
    title: "Open account in Stripe",
    icon: StripeIcon,
    matches: ["Open account in Stripe", "Stripe account"],
    hide: HIDE_IF_NOT_ADMIN,
    action: () => {
      const accountStore = useAccountStore();
      const stripeAccountId = accountStore.newsletter?.stripe_account;
      if (stripeAccountId) {
        window.open(
          `https://dashboard.stripe.com/acct_1AWoe7LKt3XXUJx8/connect/view-as/${stripeAccountId}`,
          "_blank"
        );
      } else {
        console.error("No Stripe account ID found");
      }
    },
  },
  "/comments": {
    title: "Comments",
    icon: ChatBubbleLeftRightIcon,
    route: {
      name: "/comments",
    },
    feature: "comments",
    permission: "subscriber",
  },
  "/emails": {
    title: "Emails",
    icon: EnvelopeIcon,
    route: {
      name: "/emails",
    },
    permission: "email",
  },
  "/emails/new": {
    title: "Create a new email",
    icon: EnvelopeIcon,
    route: {
      name: "/emails/new",
    },
    permission: "email",
    hide: (p) => p.email !== "write",
  },
  "/analytics": {
    title: "Analytics",
    icon: ChartBarIcon,
    route: {
      name: "/analytics",
    },
  },
  "/automations": {
    title: "Automations",
    icon: SparkleIcon,
    route: {
      name: "/automations",
    },
    feature: "automations",
    permission: "automations",
  },
  "/automations/new": {
    title: "Create a new automation",
    icon: SparkleIcon,
    route: {
      name: "/automations/new",
    },
    feature: "automations",
    permission: "automations",
    hide: (p) => p.automations !== "write",
  },
  "/surveys": {
    title: "Surveys",
    icon: ClipboardIcon,
    route: {
      name: "/surveys",
    },
    feature: "surveys",
    permission: "surveys",
  },
  "/surveys/new": {
    title: "Create a new survey",
    icon: UserGroupIcon,
    route: {
      name: "/surveys/new",
    },
    feature: "surveys",
    permission: "surveys",
    hide: (p) => p.surveys !== "write",
  },
  "/subscribers": {
    title: "Subscribers",
    icon: UserGroupIcon,
    route: {
      name: "/subscribers",
    },
    permission: "subscriber",
  },
  "/tags": {
    title: "Tags",
    icon: TagIcon,
    route: {
      name: "/tags",
    },
    feature: "tags",
  },
  "/tags/new": {
    title: "Create a new tag",
    icon: TagIcon,
    route: {
      name: "/tags/new",
    },
    feature: "tags",
  },
  "/webhooks": {
    title: "Webhooks",
    icon: CodeBracketIcon,
    route: {
      name: "/webhooks",
    },
    feature: "webhooks",
  },
  "/webhooks/new": {
    title: "Create a new webhook",
    icon: CodeBracketIcon,
    route: {
      name: "/webhooks/new",
    },
  },
  "/settings/general": {
    title: "General",
    matches: [
      "Features",
      "Locale",
      "Settings",
      "Timezone",
      "Brand",
      "Tint",
      "Icon",
      "Share image",
    ],
    icon: CogIcon,
    route: {
      name: "/settings/general",
    },
  },
  "/settings/email": {
    title: "Email",
    matches: ["Email template", "CSS", "Header", "Footer"],
    icon: PaintBrushIcon,
    route: {
      name: "/settings/email",
    },
  },
  "/settings/email.css": {
    title: "Email CSS",
    matches: ["Email CSS", "Email styles", "Custom CSS"],
    icon: CodeBracketIcon,
    route: {
      name: "/settings/email.css",
    },
  },
  "/settings/snippets": {
    title: "Snippets",
    matches: [
      "Snippets",
      "Reusable content",
      "Content blocks",
      "Template snippets",
    ],
    icon: DocumentTextIcon,
    route: {
      name: "/settings/snippets",
    },
  },
  "/settings/snippets.new": {
    title: "Create a new snippet",
    icon: DocumentTextIcon,
    route: {
      name: "/settings/snippets.new",
    },
  },
  "/settings/books": {
    title: "Books",
    matches: ["Books", "Author books", "Bookshelf"],
    icon: BookOpenIcon,
    route: {
      name: "/settings/books",
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/settings/books.new": {
    title: "Add a new book",
    icon: BookOpenIcon,
    route: {
      name: "/settings/books.new",
    },
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/settings/archives": {
    title: "Web",
    matches: [
      "Web template",
      "Web",
      "CSS",
      "Header",
      "Footer",
      "Social sharing",
    ],
    icon: ArchiveBoxIcon,
    route: {
      name: "/settings/archives",
    },
  },
  "/settings/archives.css": {
    title: "Web CSS",
    matches: ["Web CSS", "Web styles", "Archive CSS", "Custom CSS"],
    icon: CodeBracketIcon,
    route: {
      name: "/settings/archives.css",
    },
  },
  "/settings/archives.announcement": {
    title: "Announcement bar",
    matches: [
      "Announcement",
      "Banner",
      "Archive banner",
      "Archive announcement",
    ],
    icon: BellIcon,
    route: {
      name: "/settings/archives.announcement",
    },
  },
  "/settings/team": {
    title: "Team",
    icon: UserGroupIcon,
    hide: (p) => p.administrivia === undefined,
    route: {
      name: "/settings/team",
    },
  },
  "/settings/firewall": {
    title: "Firewall",
    icon: ViewfinderIcon,
    matches: ["Spam", "firewall"],
    route: {
      name: "/settings/firewall",
    },
  },
  "/settings/domains.hosting": {
    title: "Hosting",
    matches: ["Hosting domain", "Custom domain", "CNAME"],
    icon: ComputerDesktopIcon,
    route: {
      name: "/settings/domains.hosting",
    },
  },
  "/settings/domains.sending": {
    title: "Sending",
    matches: ["Sending domain", "Email domain", "DNS", "SPF", "DKIM"],
    icon: EnvelopeIcon,
    route: {
      name: "/settings/domains.sending",
    },
  },
  "/settings/team/new": {
    title: "Invite a new team member",
    icon: UserIcon,
    route: {
      name: "/settings/team/new",
    },
  },
  "/emails/imports/new": {
    title: "Import emails",
    icon: EnvelopeIcon,
    route: {
      name: "/emails/imports/new",
    },
    permission: "email",
    hide: (p) => p.email !== "write",
  },
  "/subscribers/imports/new": {
    title: "Import subscribers",
    icon: UserGroupIcon,
    route: {
      name: "/subscribers/imports/new",
    },
    permission: "subscriber",
    hide: (p) => p.subscriber !== "write",
  },
  "/events": {
    title: "Events",
    icon: CalendarIcon,
    route: {
      name: "/events",
    },
  },
  "/forms": {
    title: "Forms",
    icon: DocumentTextIcon,
    route: {
      name: "/forms",
    },
    permission: "forms",
    hide: HIDE_IF_NOT_ADMIN,
  },
  "/forms.new": {
    title: "Create a new form",
    icon: DocumentTextIcon,
    route: {
      name: "/forms.new",
    },
    permission: "forms",
    hide: (p, n) => !IS_ADMIN || p.forms !== "write",
  },
  "/feeds": {
    title: "Feeds",
    icon: RSSIcon,
    route: {
      name: "/feeds",
    },
    matches: ["RSS", "Feeds"],
    feature: "rss",
  },
  "/feeds/new": {
    title: "Connect an RSS feed",
    icon: RSSIcon,
    route: {
      name: "/feeds/new",
    },
    feature: "rss",
  },
  "/requests": {
    title: "Requests",
    icon: CodeBracketIcon,
    route: {
      name: "/requests",
    },
    feature: "api",
  },
  "/keys": {
    title: "Keys",
    icon: KeyIcon,
    route: {
      name: "/keys",
    },
    feature: "api",
  },
  "/sponsorships": {
    title: "Sponsorships",
    icon: ShoppingBagIcon,
    route: {
      name: "/sponsorships",
    },
    feature: "sponsorships",
  },
  "/settings/billing": {
    title: "Billing",
    icon: CreditCardIcon,
    route: {
      name: "/settings/billing",
    },
    matches: ["Billing", "Invoices"],
  },
  "/settings/integrations": {
    title: "Integrations",
    icon: PlayIcon,
    hide: (p) => p.administrivia === undefined,
    route: {
      name: "/settings/integrations",
    },
  },
  "/settings/embedding": {
    title: "Embedding",
    icon: CodeBracketIcon,
    route: {
      name: "/settings/embedding",
    },
  },
  "/settings/paid-subscriptions": {
    title: "Paid subscriptions",
    icon: CreditCardIcon,
    route: {
      name: "/settings/paid-subscriptions",
    },
    feature: "paid_subscriptions",
    matches: ["Premium", "Stripe"],
  },
  "/settings/subscribing": {
    title: "Subscribing",
    matches: ["Unsubscription reasons", "Clean"],
    icon: RSSIcon,
    hide: (p) => p.administrivia === undefined,
    route: {
      name: "/settings/subscribing",
    },
  },
  "/settings/subscribing.gift": {
    title: "Gift subscription notification",
    icon: GiftIcon,
    route: {
      name: "/settings/subscribing.gift",
    },
    hide: (p, n) => n.paid_subscriptions_status !== "active",
  },
  "/settings/tracking": {
    title: "Tracking",
    icon: EyeIcon,
    matches: [
      "Click tracking",
      "Open tracking",
      "UTM",
      "Page views",
      "Pageview",
    ],
    route: {
      name: "/settings/tracking",
    },
  },
  "/settings/security": {
    title: "Sign in & security",
    matches: ["Redirect to dashboard", "Username", "Password"],
    icon: LockClosedIcon,
    route: {
      name: "/settings/security",
    },
  },
  "/settings/notifications": {
    title: "Notifications",
    icon: BellIcon,
    route: {
      name: "/settings/notifications",
    },
  },
  "/settings/referrals": {
    title: "Referrals",
    icon: GiftIcon,
    route: {
      name: "/settings/referrals",
    },
  },
  "/__special_actions/portal": {
    title: "Subscriptions",
    icon: BookOpenIcon,
    action: () => {
      window.open("/portal/dashboard", "_blank");
    },
  },
  "/settings/danger-zone": {
    title: "Danger Zone",
    icon: ExclamationTriangleIcon,
    matches: ["Delete", "Delete account", "Delete data", "Test mode"],
    route: {
      name: "/settings/danger-zone",
    },
  },
  "/settings/files": {
    title: "Files",
    matches: [
      "Attachments",
      "Data",
      "Exports",
      "Images",
      "Import archive",
      "Import subscribers",
      "Imports",
    ],
    icon: DocumentArrowUpIcon,
    route: {
      name: "/settings/files",
    },
  },
  "/settings/portal": {
    title: "Subscriber portal",
    matches: ["Portal", "Autonomy"],
    icon: StackMicroIcon,
    route: {
      name: "/settings/portal",
    },
  },
  "/settings/general.description": {
    title: "Description",
    icon: EnvelopeIcon,
    matches: ["Description"],
    route: {
      name: "/settings/general.description",
    },
  },
  "/home": {
    title: "Home",
    icon: HouseIcon,
    route: {
      name: "/home",
    },
  },
  "/inbox": {
    title: `Inbox`,
    icon: InboxIcon,
    route: {
      name: "/inbox",
    },
    permission: "email",
  },
  "/platform": {
    title: "Platform",
    icon: StackMicroIcon,
    route: {
      name: "/platform",
    },
  },
  "/settings/subscribing.confirmation": {
    title: "Confirmation email",
    icon: EnvelopeIcon,
    route: {
      name: "/settings/subscribing.confirmation",
    },
    matches: ["Subscription", "Confirmation", "Double opt-in"],
  },
  "/settings/subscribing.welcome": {
    title: "Welcome email",
    icon: EnvelopeIcon,
    route: {
      name: "/settings/subscribing.welcome",
    },
    matches: ["Welcome", "Welcome email"],
  },
} as const satisfies RouteToConfiguration;
